import { useCallback, useEffect, useState } from 'react';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

interface UseTelemetryOptions {
  enabled: boolean;
  endpoint: string | null;
  pollIntervalMs: number;
  headers?: Record<string, string>;
  orgId?: string | null;
  agentId?: string | null;
}

function buildTelemetryUrl(endpoint: string, params: Record<string, string | null | undefined>) {
  const hasParams = Object.values(params).some(value => value);
  if (!hasParams) return endpoint;

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(endpoint, base);
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  } catch {
    const query = Object.entries(params)
      .filter(([, value]) => value)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&');
    if (!query) return endpoint;
    return endpoint.includes('?') ? `${endpoint}&${query}` : `${endpoint}?${query}`;
  }
}

export function useTelemetry({
  enabled,
  endpoint,
  pollIntervalMs,
  headers,
  orgId,
  agentId,
}: UseTelemetryOptions) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchTelemetry = useCallback(async () => {
    if (!enabled || !endpoint) return;
    const url = buildTelemetryUrl(endpoint, {
      orgId: orgId || undefined,
      agentId: agentId || undefined,
    });
    setLoading(true);
    try {
      const res = await fetchWithTimeout(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(headers || {}),
        },
        timeoutMs: 10_000,
      });
      if (!res.ok) throw new Error(`Telemetry failed: ${res.status}`);
      const payload = await res.json();
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Telemetry error');
    } finally {
      setLoading(false);
    }
  }, [enabled, endpoint, headers, orgId, agentId]);

  useEffect(() => {
    if (!enabled || !endpoint) return;
    fetchTelemetry();
    if (!pollIntervalMs || pollIntervalMs <= 0) return;
    const id = setInterval(fetchTelemetry, pollIntervalMs);
    return () => clearInterval(id);
  }, [enabled, endpoint, pollIntervalMs, fetchTelemetry]);

  return { data, error, loading, refresh: fetchTelemetry };
}
